import {
  CreateLoanDTO,
  Loan,
  LoanFilters,
  LoanQueryResult,
  LoanSummary,
  UpdateLoanDTO,
  LoanRepaymentDTO,
  LoanPortfolioMetrics,
} from 'src/types/loan.types';
import {
  calculateLoanRepayment,
  generateAmortizationSchedule,
  calculateLoanProgress,
  AmortizationInstallment,
} from 'src/utils/loanCalculator';
import { supabase } from './supabase/client';
import { transactionService } from './transaction.service';

import { resolveAssignedStudentIdsForTeacher } from './student.service';

const TABLE_NAME = 'loans';
const TRANSACTION_TYPES_TABLE = 'transaction_types';

function applyLoanDateFilters(query: any, startDate?: string, endDate?: string) {
  let q = query;
  if (startDate) q = q.gte('createdat', startDate);
  if (endDate) q = q.lte('createdat', endDate);
  return q;
}

function applyLoanFilters(
  query: any,
  filters: {
    assignedStudentIds: string[] | null;
    studentId?: string;
    status?: string;
    grade?: string | number;
    startDate?: string;
    endDate?: string;
  }
) {
  let q = query;
  if (filters.assignedStudentIds !== null) q = q.in('studentid', filters.assignedStudentIds);
  if (filters.studentId) q = q.eq('studentid', filters.studentId);
  if (filters.status && filters.status !== 'ALL') q = q.eq('status', filters.status);
  if (filters.grade && filters.grade !== 'ALL') q = q.eq('student.grade', String(filters.grade));
  q = applyLoanDateFilters(q, filters.startDate, filters.endDate);
  return q.is('deletedat', null).is('student.user.deletedat', null);
}

function filterLoansBySearchTerm(loans: Loan[], search?: string): Loan[] {
  if (!search || search.trim() === '') return loans;
  const term = search.toLowerCase().trim();
  return loans.filter((loan) => {
    const reasonMatch = (loan.reason || '').toLowerCase().includes(term);
    const firstName = (loan.student?.user?.firstname || '').toLowerCase();
    const lastName = (loan.student?.user?.lastname || '').toLowerCase();
    const email = (loan.student?.user?.email || '').toLowerCase();
    const fullName = `${firstName} ${lastName}`;
    return (
      reasonMatch ||
      firstName.includes(term) ||
      lastName.includes(term) ||
      fullName.includes(term) ||
      email.includes(term)
    );
  });
}

/**
 * Resolve or create dynamic transaction type for loans
 */
async function getOrCreateLoanTransactionType(type: 'disbursement' | 'repayment'): Promise<string> {
  const isCredit = type === 'disbursement';
  const name = isCredit ? 'Loan Disbursement' : 'Loan Repayment';
  const description = isCredit
    ? 'Disbursement of approved educational microloan funds'
    : 'Scholar repayment towards active microloan balance';

  // 1. Search for existing type
  const { data: existing, error: searchError } = await supabase
    .from(TRANSACTION_TYPES_TABLE)
    .select('id')
    .eq('name', name)
    .is('deletedat', null)
    .maybeSingle();

  if (searchError) {
    console.warn(`[loanService] Error looking up transaction type '${name}':`, searchError);
  }

  if (existing?.id) {
    return existing.id;
  }

  // 2. Fallback: try finding any transaction type of matching type (credit / debit)
  const { data: fallback } = await supabase
    .from(TRANSACTION_TYPES_TABLE)
    .select('id')
    .eq('type', isCredit ? 'credit' : 'debit')
    .is('deletedat', null)
    .limit(1)
    .maybeSingle();

  if (fallback?.id) {
    return fallback.id;
  }

  // 3. Insert new transaction type if allowed
  try {
    const { data: inserted, error: insertError } = await supabase
      .from(TRANSACTION_TYPES_TABLE)
      .insert({
        name,
        description,
        type: isCredit ? 'credit' : 'debit',
        points: 0,
        extendedproperties: {
          category: 'loans',
          autoCreated: true,
        },
      })
      .select('id')
      .single();

    if (!insertError && inserted?.id) {
      return inserted.id;
    }
  } catch (e) {
    console.warn(`[loanService] Unable to create transaction type '${name}', using fallback:`, e);
  }

  // Default system fallback
  return isCredit ? '13bc4ab4-00ea-4afc-9d40-0d2a0a0c49c8' : '24cd5bc5-11fa-5bfc-0e51-1e3b1b1d50d9';
}

export const loanService = {
  /**
   * Create a new loan request with generated amortization schedule
   */
  async createLoan(dto: CreateLoanDTO): Promise<Loan> {
    try {
      const calculation = calculateLoanRepayment(dto.amount, dto.term_months, dto.interest_rate);
      const schedule = generateAmortizationSchedule(dto.amount, dto.term_months, dto.interest_rate);

      // Set terminal due date
      const dueDate = new Date();
      dueDate.setMonth(dueDate.getMonth() + dto.term_months);

      const extendedproperties = {
        ...(dto.extendedproperties || {}),
        amortizationSchedule: schedule,
        originalAmount: dto.amount,
        originalTerm: dto.term_months,
        originalInterestRate: dto.interest_rate,
      };

      const { data, error } = await supabase
        .from(TABLE_NAME)
        .insert({
          studentid: dto.studentid,
          amount: dto.amount,
          interest_rate: dto.interest_rate,
          term_months: dto.term_months,
          reason: dto.reason,
          monthly_payment: calculation.monthlyPayment,
          total_amount: calculation.totalRepayment,
          paid_amount: 0,
          status: 'Pending',
          due_date: dueDate.toISOString(),
          extendedproperties,
        })
        .select('*, student:students(*,user:users(*))')
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('[loanService.createLoan]:', error);
      throw error;
    }
  },

  /**
   * Get loans with pagination, search, status, grade, and teacher scoping
   */
  async getLoans({
    studentId,
    teacherId,
    teacherUserId,
    scope,
    cohortId,
    grade,
    status,
    search,
    startDate,
    endDate,
    page = 1,
    limit = 10,
  }: LoanFilters = {}): Promise<LoanQueryResult> {
    try {
      // 1. Resolve teacher scoping
      const assignedStudentIds = await resolveAssignedStudentIdsForTeacher({
        teacherId,
        teacherUserId,
        scope,
      });

      if (assignedStudentIds !== null && assignedStudentIds.length === 0) {
        return { data: [], count: 0 };
      }

      let query = supabase
        .from(TABLE_NAME)
        .select('*, student:students!inner(*,user:users!inner(*))', { count: 'exact' });

      query = applyLoanFilters(query, {
        assignedStudentIds,
        studentId,
        status,
        grade,
        startDate,
        endDate,
      });

      // Add sorting and pagination
      const from = (page - 1) * limit;
      const to = from + limit - 1;
      query = query.order('createdat', { ascending: false }).range(from, to);

      const { data, error, count } = await query;
      if (error) throw error;

      const loans = filterLoansBySearchTerm((data || []) as Loan[], search);

      return {
        data: loans,
        count: count || loans.length,
      };
    } catch (error) {
      console.error('[loanService.getLoans]:', error);
      throw error;
    }
  },

  /**
   * Get loan by ID
   */
  async getLoanById(id: string): Promise<Loan | null> {
    try {
      const { data, error } = await supabase
        .from(TABLE_NAME)
        .select('*, student:students(*,user:users(*))')
        .eq('id', id)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('[loanService.getLoanById]:', error);
      throw error;
    }
  },

  /**
   * Update loan
   */
  async updateLoan(id: string, dto: UpdateLoanDTO): Promise<Loan> {
    try {
      const { data, error } = await supabase
        .from(TABLE_NAME)
        .update({
          ...dto,
          updatedat: new Date().toISOString(),
        })
        .eq('id', id)
        .select('*, student:students(*,user:users(*))')
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('[loanService.updateLoan]:', error);
      throw error;
    }
  },

  /**
   * Approve a loan and disburse funds to student's checking balance
   */
  async approveLoan(
    loanId: string,
    options?: {
      approvedBy?: { id?: string; name?: string; role?: string };
    }
  ): Promise<Loan> {
    try {
      const loan = await this.getLoanById(loanId);
      if (!loan) throw new Error('Loan not found');
      if (loan.status === 'Approved') throw new Error('Loan is already approved');
      // Only a request still waiting for a decision can be paid out; Denied, Paid and Overdue loans cannot.
      if (loan.status !== 'Pending') throw new Error(`A ${loan.status.toLowerCase()} loan cannot be approved`);

      const studentId = loan.studentid;
      const principal = Number(loan.amount) || 0;

      // 1. Resolve transaction type for disbursement
      const typeId = await getOrCreateLoanTransactionType('disbursement');

      // 2. Create credit transaction to deposit funds into student's wallet
      await transactionService.createTransaction({
        studentId,
        transactionTypeId: typeId,
        amount: principal,
        description: `Loan Disbursement · ${loan.reason}`,
        reason: 'Educational Microloan',
        extendedProperties: {
          originalAmount: principal,
          loanId: loan.id,
          loanAmount: principal,
          loanTermMonths: loan.term_months,
          interestRate: loan.interest_rate,
          category: 'loans',
        },
      });

      // 3. Mark loan as Approved
      const updated = await this.updateLoan(loanId, {
        status: 'Approved',
        extendedproperties: {
          ...(loan.extendedproperties || {}),
          approvedBy: {
            id: options?.approvedBy?.id,
            name: options?.approvedBy?.name,
            role: options?.approvedBy?.role || 'Educator',
            date: new Date().toISOString(),
          },
        },
      });

      return updated;
    } catch (error) {
      console.error('[loanService.approveLoan]:', error);
      throw error;
    }
  },

  /**
   * Counter-offer a loan (modify principal, term, or rate)
   */
  async counterOfferLoan(
    loanId: string,
    params: {
      amount: number;
      termMonths: number;
      interestRate: number;
      note?: string;
      offeredBy?: string;
    }
  ): Promise<Loan> {
    try {
      const loan = await this.getLoanById(loanId);
      if (!loan) throw new Error('Loan not found');

      const calculation = calculateLoanRepayment(params.amount, params.termMonths, params.interestRate);
      const schedule = generateAmortizationSchedule(params.amount, params.termMonths, params.interestRate);

      const dueDate = new Date();
      dueDate.setMonth(dueDate.getMonth() + params.termMonths);

      const counterOfferData = {
        amount: params.amount,
        termMonths: params.termMonths,
        interestRate: params.interestRate,
        monthlyPayment: calculation.monthlyPayment,
        totalAmount: calculation.totalRepayment,
        note: params.note,
        offeredBy: params.offeredBy,
        offeredAt: new Date().toISOString(),
      };

      const extendedproperties = {
        ...(loan.extendedproperties || {}),
        amortizationSchedule: schedule,
        counterOffer: counterOfferData,
      };

      const updated = await this.updateLoan(loanId, {
        amount: params.amount,
        term_months: params.termMonths,
        interest_rate: params.interestRate,
        monthly_payment: calculation.monthlyPayment,
        total_amount: calculation.totalRepayment,
        due_date: dueDate.toISOString(),
        extendedproperties,
      });

      return updated;
    } catch (error) {
      console.error('[loanService.counterOfferLoan]:', error);
      throw error;
    }
  },

  /**
   * Reject a loan with coaching feedback
   */
  async rejectLoan(
    loanId: string,
    params: {
      reason: string;
      category?: string;
    }
  ): Promise<Loan> {
    try {
      const loan = await this.getLoanById(loanId);
      if (!loan) throw new Error('Loan not found');

      const extendedproperties = {
        ...(loan.extendedproperties || {}),
        rejectionReason: params.reason,
        rejectionCategory: params.category || 'general',
      };

      const updated = await this.updateLoan(loanId, {
        status: 'Denied',
        extendedproperties,
      });

      return updated;
    } catch (error) {
      console.error('[loanService.rejectLoan]:', error);
      throw error;
    }
  },

  /**
   * Execute a payment on a loan (Student or Teacher initiated)
   */
  async repayLoan(dto: LoanRepaymentDTO): Promise<Loan> {
    try {
      const loan = await this.getLoanById(dto.loanId);
      if (!loan) throw new Error('Loan not found');
      // Money was never paid out for a Pending or Denied loan, so there is nothing to repay.
      if (loan.status === 'Pending' || loan.status === 'Denied') {
        throw new Error('Only an approved loan can be repaid.');
      }

      const paymentAmount = Math.max(0.01, Math.round(Number(dto.amount) * 100) / 100);
      const totalAmount = Number(loan.total_amount) || Number(loan.amount);
      const currentPaid = Number(loan.paid_amount) || 0;
      const remainingBalance = Math.max(0, Math.round((totalAmount - currentPaid) * 100) / 100);

      // Cannot pay more than remaining balance
      const actualPayment = Math.min(paymentAmount, remainingBalance);
      if (actualPayment <= 0) {
        throw new Error('This loan is already fully paid off.');
      }

      // The borrower always pays their own loan, so the balance must cover the payment.
      const borrowerId = loan.studentid;
      const { data: borrower, error: borrowerError } = await supabase
        .from('students')
        .select('balance')
        .eq('id', borrowerId)
        .single();
      if (borrowerError) throw borrowerError;
      if (Number(borrower?.balance || 0) < actualPayment) {
        throw new Error('Not enough balance to make this payment.');
      }

      // 1. Resolve transaction type for repayment (debit)
      const typeId = await getOrCreateLoanTransactionType('repayment');

      // 2. Create debit transaction against student wallet
      const { id: transactionId, settled } = await transactionService.createTransaction({
        studentId: borrowerId,
        transactionTypeId: typeId,
        amount: actualPayment,
        description: `Loan Repayment · ${loan.reason}`,
        reason: 'Loan Repayment',
        extendedProperties: {
          loanId: loan.id,
          paymentMethod: dto.paymentMethod || 'manual_student',
          note: dto.note,
          category: 'loans',
        },
      });

      // An auto-charge repayment is already taken. If the repayment type is a bill, it waits for the student;
      // a repayment is the student's own choice, so settle it now.
      // ponytail: two steps, not atomic. If this fails the debit stays pending and the loan is not credited.
      // Keep the repayment type on auto-charge (create_debit, one step) to avoid this path.
      if (!settled) {
        await transactionService.respondToTransaction(transactionId, borrowerId, 'approve', { payment_source: 'balance' });
      }

      // 3. Update loan paid_amount and status
      const newPaidAmount = Math.round((currentPaid + actualPayment) * 100) / 100;
      const isNowPaid = newPaidAmount >= totalAmount;
      const newStatus = isNowPaid ? 'Paid' : loan.status;

      // 4. Update amortization schedule status
      const schedule: AmortizationInstallment[] = loan.extendedproperties?.amortizationSchedule || [];
      let runningCovered = newPaidAmount;
      const updatedSchedule = schedule.map((inst) => {
        if (runningCovered >= inst.amount) {
          runningCovered -= inst.amount;
          return {
            ...inst,
            status: 'paid' as const,
            paidAt: inst.paidAt || new Date().toISOString(),
          };
        }
        return inst;
      });

      // 5. Append payment record
      const payments = loan.extendedproperties?.payments || [];
      payments.push({
        id: `pay-${Date.now()}`,
        amount: actualPayment,
        paymentDate: new Date().toISOString(),
        transactionId,
        method: dto.paymentMethod || 'manual_student',
        note: dto.note,
        remainingBalanceAfter: Math.max(0, Math.round((totalAmount - newPaidAmount) * 100) / 100),
      });

      const updated = await this.updateLoan(dto.loanId, {
        paid_amount: newPaidAmount,
        status: newStatus,
        extendedproperties: {
          ...(loan.extendedproperties || {}),
          amortizationSchedule: updatedSchedule,
          payments,
        },
      });

      return updated;
    } catch (error) {
      console.error('[loanService.repayLoan]:', error);
      throw error;
    }
  },

  /**
   * Get loan portfolio summary metrics across classroom or school
   */
  async getLoanPortfolioSummary(filters?: {
    teacherId?: string;
    teacherUserId?: string;
    scope?: 'all' | 'assigned';
  }): Promise<LoanPortfolioMetrics> {
    try {
      const { data: allLoans } = await this.getLoans({
        ...filters,
        limit: 1000,
      });

      let totalLoans = 0;
      let activeLoansCount = 0;
      let pendingLoansCount = 0;
      let paidLoansCount = 0;
      let overdueLoansCount = 0;
      let totalLent = 0;
      let totalPaid = 0;

      allLoans.forEach((l) => {
        totalLoans += 1;
        const total = Number(l.total_amount) || Number(l.amount) || 0;
        const paid = Number(l.paid_amount) || 0;

        if (l.status === 'Pending') {
          pendingLoansCount += 1;
        } else if (l.status === 'Approved') {
          activeLoansCount += 1;
          totalLent += total;
          totalPaid += paid;
        } else if (l.status === 'Paid') {
          paidLoansCount += 1;
          totalLent += total;
          totalPaid += paid;
        } else if (l.status === 'Overdue') {
          overdueLoansCount += 1;
          totalLent += total;
          totalPaid += paid;
        }
      });

      const totalOutstanding = Math.max(0, Math.round((totalLent - totalPaid) * 100) / 100);
      const repaymentRate = totalLent > 0 ? Math.round((totalPaid / totalLent) * 100) : 100;

      return {
        totalLoans,
        activeLoansCount,
        pendingLoansCount,
        paidLoansCount,
        overdueLoansCount,
        totalLent: Math.round(totalLent * 100) / 100,
        totalOutstanding,
        totalPaid: Math.round(totalPaid * 100) / 100,
        repaymentRate,
      };
    } catch (error) {
      console.error('[loanService.getLoanPortfolioSummary]:', error);
      throw error;
    }
  },

  /**
   * Get loan summary for a student
   */
  async getLoanSummary(studentId: string): Promise<LoanSummary | null> {
    try {
      const { data, error } = await supabase
        .from('loan_summaries')
        .select('*')
        .eq('studentid', studentId)
        .maybeSingle();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('[loanService.getLoanSummary]:', error);
      throw error;
    }
  },

  /**
   * Delete loan (soft delete)
   */
  async deleteLoan(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from(TABLE_NAME)
        .update({ deletedat: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error('[loanService.deleteLoan]:', error);
      throw error;
    }
  },
};
